// The intermediary's registration number on THIS offer.
//
// Each individual advisor holds their own registration (FINMA for a Swiss
// tenant), so the number is entered per offer and never read from the broker
// company record. The field is empty by default — on a new offer and on every
// new version or revision — and the document prints "<label> n° ______" to be
// completed by hand until a number is entered here.
//
// Its label is the tenant's offerDocuments.brokerRegistrationAuthority, the
// same string the document prints beside the number.

import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { S, Icon } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
const MAX = 40;

/** The field label: "<authority> n°" when the tenant names one, else the generic wording. */
export function registrationFieldLabel(authority, t) {
  const a = String(authority || '').trim();
  return a
    ? t('offers.detail.registrationNumberWithAuthority', { authority: a })
    : t('offers.detail.registrationNumber');
}

const RegistrationNumberField = ({ offer, canEdit, onUpdated }) => {
  const { t } = useTranslation();
  const authority = useSelector(state => state?.tenantConfig?.config?.offerDocuments?.brokerRegistrationAuthority) || '';
  const stored = offer?.intermediaryRegistrationNumber || '';
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(stored);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (!editing) setValue(stored); }, [stored, editing]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      const resp = await axios.patch(
        `${BASE_URL}/offers/${offer._id}`,
        { intermediaryRegistrationNumber: value.trim() },
        { withCredentials: true },
      );
      setEditing(false);
      onUpdated && onUpdated(resp?.data?.data || null);
    } catch (err) {
      setError(err.response?.data?.message || t('offers.detail.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const label = registrationFieldLabel(authority, t);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label htmlFor='offer-registration-number' style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {label}
        </label>
        {canEdit && !editing && (
          <button type='button' onClick={() => setEditing(true)} style={{ fontSize: 12, color: S.burgundy, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
            <Icon name='edit' color={S.burgundy} size={12} /> {t('offers.detail.edit')}
          </button>
        )}
        {editing && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type='button' onClick={() => { setEditing(false); setValue(stored); setError(''); }} style={{ fontSize: 12, color: S.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}>
              {t('offers.detail.cancel')}
            </button>
            <button type='button' onClick={save} disabled={saving || value.trim().length > MAX} style={{ fontSize: 12, color: '#fff', background: S.burgundy, border: 'none', padding: '4px 10px', borderRadius: 4, cursor: saving ? 'wait' : 'pointer', fontWeight: 600 }}>
              {saving ? t('offers.detail.saving') : t('offers.detail.save')}
            </button>
          </div>
        )}
      </div>
      {editing ? (
        <input
          id='offer-registration-number'
          value={value}
          maxLength={MAX}
          onChange={e => setValue(e.target.value)}
          placeholder={t('offers.detail.registrationNumberPlaceholder')}
          style={{ width: '100%', padding: '8px 10px', border: `1px solid ${S.borderLight}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
        />
      ) : (
        <div id='offer-registration-number' style={{ fontSize: 13, color: stored ? S.textPrimary : S.textMuted }}>
          {stored || t('offers.detail.registrationNumberEmpty')}
        </div>
      )}
      {error && <div style={{ marginTop: 6, fontSize: 12, color: '#92400e' }}>{error}</div>}
    </div>
  );
};

export default RegistrationNumberField;
